import { useState } from 'react';
import { MarkdownPreview } from '../publisher/MarkdownPreview';

interface HeroCoverProps {
  book: {
    title?: string;
    author?: string;
    blurb?: string;
    coverUrl?: string;
    chapterCount?: number;
  } | null | undefined;
}

export function HeroCover({ book }: HeroCoverProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="relative min-h-[85vh] flex flex-col justify-end pb-12">
      {/* Background Image with Parallax */}
      <div className="absolute inset-0 overflow-hidden">
        {book?.coverUrl ? (
          <>
            <img 
              src={book.coverUrl} 
              alt={book.title}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${
                isLoaded ? 'scale-100 opacity-100' : 'scale-110 opacity-0'
              }`}
              onLoad={() => setIsLoaded(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)] via-[var(--bg-primary)]/80 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-primary)]/50 via-transparent to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-secondary)] to-[var(--bg-primary)]" />
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
        {/* Genre Tags */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <span className="px-3 py-1 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] font-ui text-[10px] uppercase tracking-wider">
            Fiction
          </span>
          {book?.chapterCount && (
            <span className="px-3 py-1 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-tertiary)] font-ui text-[10px] uppercase tracking-wider">
              {book.chapterCount} Chapters
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="font-body text-4xl md:text-5xl lg:text-6xl font-medium text-[var(--text-primary)] mb-4 leading-tight">
          {book?.title || 'Loading...'}
        </h1>

        {/* Author */}
        <p className="font-ui text-lg text-[var(--text-secondary)] mb-6">
          by <span className="text-[var(--text-primary)]">{book?.author || 'Unknown'}</span>
        </p>

        {/* Blurb with Read More */}
        <div className="mb-8">
          <div 
            className={`font-body text-base text-[var(--text-secondary)] leading-relaxed max-w-lg mx-auto overflow-y-auto transition-all duration-300 scrollbar-thin scrollbar-thumb-[var(--accent)] scrollbar-track-transparent ${
              isExpanded ? 'max-h-[60vh] pr-2' : 'max-h-[120px]'
            }`}
          >
            {book?.blurb ? (
              <MarkdownPreview markdown={book.blurb} />
            ) : (
              'A captivating story awaits...'
            )}
          </div>
          
          {/* Read More Button */}
          {book?.blurb && book.blurb.length > 200 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-4 text-[var(--accent)] font-ui text-sm uppercase tracking-wider hover:underline transition-all"
            >
              {isExpanded ? 'Show Less' : 'Read More'}
            </button>
          )}
        </div>

        {/* Scroll Indicator */}
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="font-ui text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider">Scroll to read</span>
          <svg className="w-5 h-5 text-[var(--text-tertiary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </div>
  );
}
